import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, Wand2, Trash2, Pencil, Search, Plus, X, Check,
    Sparkles, Bot, User as UserIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
    "Travel", "Hotel", "Food & Dining", "Fuel",
    "Office Expense", "IT / Software", "Subscription", "Shopping",
    "Utility Bills", "Vendor Payment", "Client Expense", "Personal Expense",
    "Medical", "Insurance", "Education", "Repair & Maintenance",
    "EMI", "Tax / Government Payment", "Other",
];

const SOURCE_META = {
    learned: { label: "AI-learned", icon: Sparkles, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    manual:  { label: "Manual",     icon: UserIcon,  tone: "bg-stone-100 text-stone-800 border-stone-200" },
    seed:    { label: "Built-in",   icon: Bot,       tone: "bg-violet-50 text-violet-800 border-violet-200" },
};

export default function MerchantRulesAdmin() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [source, setSource] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null); // rule doc | 'new' | null

    const load = async () => {
        setBusy(true);
        try {
            const params = { limit: 300 };
            if (q) params.q = q;
            if (source) params.source = source;
            const r = await api.get("/admin/merchant-rules", { params });
            setRows(r.data?.rules || []);
        } catch { toast.error("Couldn't load rules"); }
        finally { setBusy(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async (draft) => {
        try {
            if (draft.id) {
                await api.patch(`/admin/merchant-rules/${draft.id}`, {
                    pattern: draft.pattern, category: draft.category, source: draft.source || "manual",
                });
                toast.success("Rule updated");
            } else {
                await api.post("/admin/merchant-rules", {
                    pattern: draft.pattern, category: draft.category, source: draft.source || "manual",
                });
                toast.success("Rule created");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this rule? Future matches will hit the LLM again until a new rule is learned.")) return;
        try {
            await api.delete(`/admin/merchant-rules/${id}`);
            toast.success("Rule deleted");
            load();
        } catch { toast.error("Couldn't delete"); }
    };

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="merchant-rules-admin">
            <div className="mb-6 flex items-center gap-2 text-xs">
                <Link to="/settings" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Settings
                </Link>
                <span className="text-stone-300">/</span>
                <span className="font-medium text-stone-900">Merchant rules</span>
            </div>

            <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-violet-800 font-semibold">AI Governance</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">Merchant rules.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Every time GPT categorises a merchant, VAULT persists a regex rule so the next
                        matching transaction is caught instantly — no LLM call, no cost. Fix a bad
                        classification here once and it stops repeating forever.
                    </p>
                </div>
                <Button onClick={() => setEditing({ pattern: "", category: "Other", source: "manual" })}
                        className="bg-stone-900 text-white shrink-0"
                        data-testid="merchant-rule-new">
                    <Plus size={13} className="mr-1.5" /> New rule
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search pattern or category…"
                        className="pl-8"
                        data-testid="merchant-rule-search"
                    />
                </div>
                <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40" data-testid="merchant-rule-source-filter">
                        <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All sources</SelectItem>
                        <SelectItem value="learned">AI-learned</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="seed">Built-in</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="outline" onClick={load} disabled={busy} data-testid="merchant-rule-refresh">
                    {busy ? "…" : "Refresh"}
                </Button>
            </div>

            {/* Table */}
            <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
                <div className="grid grid-cols-[1fr_1fr_120px_100px] gap-4 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                    <div>Pattern</div>
                    <div>Category</div>
                    <div>Source</div>
                    <div className="text-right">Actions</div>
                </div>
                {rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-stone-500" data-testid="merchant-rule-empty">
                        <Wand2 size={22} className="text-stone-300 mx-auto mb-2" />
                        No rules yet. Categorise a few expenses and rules will start appearing here.
                    </div>
                ) : rows.map((r) => {
                    const meta = SOURCE_META[r.source] || SOURCE_META.manual;
                    return (
                        <div key={r.id}
                             className="grid grid-cols-[1fr_1fr_120px_100px] gap-4 px-4 py-3 border-b border-stone-100 last:border-0 items-center hover:bg-stone-50"
                             data-testid={`merchant-rule-row-${r.id}`}>
                            <div className="font-mono text-[12.5px] text-stone-800 truncate">{r.pattern}</div>
                            <div className="text-[13px] text-stone-900">{r.category}</div>
                            <div>
                                <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${meta.tone}`}>
                                    <meta.icon size={9} /> {meta.label}
                                </span>
                            </div>
                            <div className="flex items-center justify-end gap-1">
                                <button onClick={() => setEditing(r)}
                                        className="w-7 h-7 rounded-md hover:bg-stone-100 inline-flex items-center justify-center text-stone-500 hover:text-stone-900"
                                        data-testid={`merchant-rule-edit-${r.id}`}>
                                    <Pencil size={12} />
                                </button>
                                <button onClick={() => remove(r.id)}
                                        className="w-7 h-7 rounded-md hover:bg-rose-50 inline-flex items-center justify-center text-stone-400 hover:text-rose-700"
                                        data-testid={`merchant-rule-delete-${r.id}`}>
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Editor dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-md" data-testid="merchant-rule-editor">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {editing?.id ? "Edit rule" : "New rule"}
                        </DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-stone-600">Pattern <span className="text-stone-400 font-normal">(regex, matched against lowercased merchant)</span></Label>
                                <Input
                                    value={editing.pattern}
                                    onChange={(e) => setEditing({ ...editing, pattern: e.target.value })}
                                    placeholder="e.g. amazon"
                                    className="mt-1.5 font-mono"
                                    data-testid="merchant-rule-pattern-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">Category</Label>
                                <Select value={editing.category}
                                        onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="merchant-rule-category-input">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>
                            <X size={13} className="mr-1.5" /> Cancel
                        </Button>
                        <Button
                            onClick={() => save(editing)}
                            disabled={!editing?.pattern?.trim() || !editing?.category}
                            className="bg-stone-900 text-white"
                            data-testid="merchant-rule-save">
                            <Check size={13} className="mr-1.5" /> Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
